import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Briefcase, Users, Heart, TrendingUp, ArrowRight, Target,
  GraduationCap, Building2, Shield, Award, Compass, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { getAuthToken } from '@/utils/secureStorage';

const API = process.env.REACT_APP_BACKEND_URL;

const coreModules = [
  {
    id: 'assessment',
    title: 'Skills Assessment',
    description: 'Take our 10-question quiz to discover your ideal career sector match.',
    icon: Compass,
    color: 'from-blue-500 to-indigo-600',
    link: '/workforce/skills-assessment'
  },
  {
    id: 'pathway',
    title: 'Your Pathway',
    description: 'Personalized 3-stage roadmap: Foundation → Skill Build → Launch.',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-600',
    link: '/workforce/pathway'
  },
  {
    id: 'employers',
    title: 'Employer Network',
    description: 'Vetted employers committed to equitable hiring and "first look" opportunities.',
    icon: Building2,
    color: 'from-purple-500 to-violet-600',
    link: '/workforce/employers'
  },
  {
    id: 'support',
    title: 'Wraparound Support',
    description: 'Childcare, transport, housing, legal—we connect you to services that remove barriers.',
    icon: Heart,
    color: 'from-rose-500 to-pink-600',
    link: '/workforce/support'
  }
];

const stages = [
  { name: 'Foundation', duration: '2-4 weeks', badge: 'Foundation Ready' },
  { name: 'Skill Build', duration: '4-12 weeks', badge: 'Sector Certified' },
  { name: 'Launch', duration: '2-4 weeks+', badge: 'Employed' }
];

export default function WorkforceHub() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [programMetrics, setProgramMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // Fetch program metrics (public)
      const metricsRes = await fetch(`${API}/api/workforce/program-metrics`);
      if (metricsRes.ok) {
        setProgramMetrics(await metricsRes.json());
      }

      // Fetch user stats if logged in
      if (user) {
        const token = getAuthToken();
        const statsRes = await fetch(`${API}/api/workforce/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50/30" data-testid="workforce-hub">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-6 bg-blue-500/20 text-blue-300 border-blue-500/30 px-4 py-1.5">
                <Briefcase className="h-4 w-4 mr-2" />
                Workforce Development (F5)
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Your Path to
                <span className="block text-blue-400">Economic Mobility</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                More than a job board. A structured pathway combining skills training, 
                work-based learning, and support services to help you build a sustainable career.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/workforce/skills-assessment">
                  <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8">
                    Start Skills Assessment
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/workforce/employers">
                  <Button size="lg" variant="outline" className="border-slate-400 text-slate-200 hover:bg-slate-800">
                    Browse Employers
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* User Progress (if enrolled) */}
      {user && stats?.enrolled && (
        <section className="py-8 -mt-8 relative z-20">
          <div className="container mx-auto px-4">
            <Card className="max-w-4xl mx-auto bg-white/90 backdrop-blur shadow-xl border-blue-200">
              <CardContent className="py-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">Your Journey</h3>
                  <Badge className="bg-blue-100 text-blue-700">
                    {stats.current_stage.replace('_', ' ')}
                  </Badge>
                </div>
                <Progress value={stats.progress_percentage} className="h-3 mb-4" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{stats.credentials_earned}</div>
                    <p className="text-sm text-slate-600">Credentials</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{stats.services_connected}</div>
                    <p className="text-sm text-slate-600">Services Connected</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{stats.employer_referrals}</div>
                    <p className="text-sm text-slate-600">Employer Referrals</p>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{stats.badges_earned?.length || 0}</div>
                    <p className="text-sm text-slate-600">PROOF Badges</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* 3-Stage Pathway */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">The 3-Stage Pathway</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A structured journey from where you are to where you want to be.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {stages.map((stage, idx) => (
                <motion.div
                  key={stage.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className={`h-full border-2 ${
                    stats?.current_stage === `Stage_${idx + 1}` 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-slate-200'
                  }`}>
                    <CardHeader className="text-center">
                      <div className="mx-auto mb-2 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xl font-bold">
                        {idx + 1}
                      </div>
                      <CardTitle>{stage.name}</CardTitle>
                      <CardDescription>{stage.duration}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <Badge variant="outline" className="text-blue-700 border-blue-300">
                        <Award className="h-3 w-3 mr-1" />
                        {stage.badge}
                      </Badge>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Modules */}
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">How We Help</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Four integrated modules working together to support your success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {coreModules.map((module, idx) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link to={module.link}>
                  <Card className="h-full hover:shadow-xl transition-all duration-300 group cursor-pointer border-2 border-transparent hover:border-blue-300">
                    <CardHeader>
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${module.color} text-white w-fit mb-3 group-hover:scale-110 transition-transform`}>
                        <module.icon className="h-6 w-6" />
                      </div>
                      <CardTitle className="text-lg text-slate-800">{module.title}</CardTitle>
                      <CardDescription className="text-slate-600 text-sm">{module.description}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Impact */}
      {programMetrics && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">Program Impact</h2>
              <p className="text-slate-600">Transparency in our outcomes.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <Card className="text-center">
                <CardContent className="py-6">
                  <div className="text-4xl font-bold text-blue-600">{programMetrics.sectors_offered}</div>
                  <p className="text-sm text-slate-600">Training Sectors</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="py-6">
                  <div className="text-4xl font-bold text-blue-600">{programMetrics.employer_partners}</div>
                  <p className="text-sm text-slate-600">Employer Partners</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="py-6">
                  <div className="text-4xl font-bold text-blue-600">{programMetrics.support_services}</div>
                  <p className="text-sm text-slate-600">Support Services</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="py-6">
                  <div className="text-4xl font-bold text-blue-600">{programMetrics.total_participants}</div>
                  <p className="text-sm text-slate-600">Participants</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Build Your Future?
          </h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            Our AI wUrkForce Coach will help you identify the right path and support you every step of the way.
          </p>
          <Link to="/workforce/assessment">
            <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8">
              Interact with AI wUrkForce Coach
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
